import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lightning, Bug, Rocket, Wrench, PencilSimple, Hammer, CaretDown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const auth = () => ({ withCredentials: true });

const KIND_STYLES = {
  launch:  { color: '#10b981', icon: Rocket,        label: 'Launch'  },
  feature: { color: '#0D9256', icon: Lightning,     label: 'Feature' },
  fix:     { color: '#D97706', icon: Bug,           label: 'Fix'     },
  infra:   { color: '#6366f1', icon: Wrench,        label: 'Infra'   },
  content: { color: '#ec4899', icon: PencilSimple,  label: 'Content' },
  build:   { color: '#64748b', icon: Hammer,        label: 'Build'   },
};

export const AdminShipLogPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/assets/shiplog`, auth())
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e.response?.status === 403 ? 'Admin only' : 'Could not load ship log');
        navigate('/');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const toggle = (idx) => {
    const next = new Set(expanded);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setExpanded(next);
  };

  const formatDate = (iso, raw) => {
    if (!iso) return raw;
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading ship log…</div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-shiplog-page">
      <SEO title="Ship Log — Admin" description="" noindex={true} />

      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate('/admin')} className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="shiplog-back-btn">
            <ArrowLeft size={14} /> Back
          </button>
          <span className="text-sm font-bold">Ship Log</span>
          <span className="text-xs text-muted-foreground">admin only</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero stats */}
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">
            <span className="bg-gradient-to-r from-emerald-500 to-emerald-300 bg-clip-text text-transparent">
              {data.count} ships
            </span>
            <span className="text-foreground"> in {data.span_days} days</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            From <span className="text-foreground font-medium">{formatDate(data.first_date)}</span> to{' '}
            <span className="text-foreground font-medium">{formatDate(data.last_date)}</span>
            {' · '}
            <span className="font-medium" style={{ color: '#10b981' }}>
              {(data.count / Math.max(data.span_days / 7, 1)).toFixed(1)} ships/week
            </span>
          </p>
          {/* Kind chips */}
          <div className="flex flex-wrap gap-2 mt-4">
            {Object.entries(data.kinds || {}).map(([kind, count]) => {
              const style = KIND_STYLES[kind] || KIND_STYLES.build;
              const Icon = style.icon;
              return (
                <span
                  key={kind}
                  className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border"
                  style={{
                    color: style.color,
                    borderColor: `${style.color}40`,
                    backgroundColor: `${style.color}12`,
                  }}
                >
                  <Icon size={11} weight="fill" /> {count} {style.label}
                </span>
              );
            })}
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gradient-to-b from-emerald-500/40 via-border to-transparent" />

          <div className="space-y-3">
            {data.milestones.map((m, idx) => {
              const style = KIND_STYLES[m.kind] || KIND_STYLES.build;
              const Icon = style.icon;
              const isOpen = expanded.has(idx);
              return (
                <div key={idx} className="relative pl-10" data-testid={`shiplog-milestone-${idx}`}>
                  {/* Dot */}
                  <div
                    className="absolute left-0 top-3 w-[22px] h-[22px] rounded-full flex items-center justify-center shadow-lg"
                    style={{
                      backgroundColor: style.color,
                      boxShadow: `0 0 0 3px var(--background), 0 0 12px ${style.color}60`,
                    }}
                  >
                    <Icon size={11} weight="fill" className="text-white" />
                  </div>

                  {/* Card */}
                  <button
                    onClick={() => toggle(idx)}
                    className="w-full text-left bg-card border border-border/50 rounded-lg px-4 py-3 hover:border-emerald-500/40 transition-colors"
                    data-testid={`shiplog-toggle-${idx}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">
                            {formatDate(m.date_iso, m.date_raw)}
                          </span>
                          <span
                            className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ color: style.color, backgroundColor: `${style.color}18` }}
                          >
                            {style.label}
                          </span>
                        </div>
                        <h3 className="text-sm md:text-base font-semibold leading-snug">
                          {m.title}
                        </h3>
                        {m.summary && !isOpen && (
                          <p className="text-xs text-muted-foreground leading-relaxed mt-1 line-clamp-2">
                            {m.summary}
                          </p>
                        )}
                      </div>
                      <CaretDown
                        size={14}
                        className={`text-muted-foreground mt-1 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </div>

                    {/* Expanded sections */}
                    {isOpen && (
                      <div className="mt-3 pt-3 border-t border-border/40 space-y-3" data-testid={`shiplog-details-${idx}`}>
                        {m.sections.filter(s => s.body).map((s, si) => (
                          <div key={si}>
                            {s.heading !== '_intro' && (
                              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                                {s.heading}
                              </div>
                            )}
                            <pre className="text-[12px] whitespace-pre-wrap font-sans leading-relaxed text-foreground/85">
                              {s.body}
                            </pre>
                          </div>
                        ))}
                      </div>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-xs text-muted-foreground italic text-center pt-10">
          Auto-parsed from <code className="font-mono">/app/memory/CHANGELOG.md</code>.
          Add a new entry there and it appears on refresh.
        </p>
      </main>
    </div>
  );
};

export default AdminShipLogPage;
